import { formatPurchaseDate } from '@/app/lib/dates'
import type { PurchaseState } from '@/app/lib/types'

import { isSaleEnded } from './isSaleEnded'

/** Parsea un ISO datetime a instante absoluto; `null` si es ausente o no
 * parseable (nunca lanza — research.md §4). */
function parseInstant(value: string | null): Date | null {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

export interface ResolvePurchaseStateInput {
  isCancelled: boolean
  purchaseText: string | null
  disablePurchase: boolean
  soldOut: boolean
  saleEnd: string | null
  saleStart: string | null
  now?: Date
}

/**
 * Resuelve el estado de compra de un evento según el orden de precedencia
 * único confirmado por negocio (contracts/resolvePurchaseState.md):
 * cancelado > texto CMS > no disponible > agotado > venta finalizada >
 * disponible el `<fecha>` > por defecto.
 */
export function resolvePurchaseState(input: ResolvePurchaseStateInput): PurchaseState {
  if (input.isCancelled) {
    return {
      kind: 'cancelled',
      imageBadge: 'Cancelado',
      ctaLabel: 'Cancelado',
      ctaEnabled: false,
    }
  }

  const purchaseText = input.purchaseText?.trim()
  if (purchaseText) {
    return {
      kind: 'custom',
      imageBadge: purchaseText,
      ctaLabel: purchaseText,
      ctaEnabled: false,
    }
  }

  if (input.disablePurchase) {
    return {
      kind: 'unavailable',
      imageBadge: 'No disponible',
      ctaLabel: 'No disponible',
      ctaEnabled: false,
    }
  }

  if (input.soldOut) {
    return {
      kind: 'sold_out',
      imageBadge: 'Agotado',
      ctaLabel: 'Agotado',
      ctaEnabled: false,
    }
  }

  const now = input.now ?? new Date()

  if (isSaleEnded(input.saleEnd, now)) {
    return {
      kind: 'sale_ended',
      imageBadge: 'Ya no se encuentra a la venta',
      ctaLabel: 'Ya no se encuentra a la venta',
      ctaEnabled: false,
    }
  }

  const saleStart = parseInstant(input.saleStart)
  if (saleStart && saleStart > now) {
    const formatted = input.saleStart ? formatPurchaseDate(input.saleStart) : null
    if (formatted) {
      const label = `Disponible el ${formatted}`
      return {
        kind: 'upcoming',
        imageBadge: label,
        ctaLabel: label,
        ctaEnabled: false,
      }
    }
  }

  return {
    kind: 'default',
    imageBadge: null,
    ctaLabel: 'Comprar',
    ctaEnabled: true,
  }
}
